/**
 * Contrat du reseau temps reel.
 *
 * Le monde est deja partage par la base (voir server/db.ts): ce qui manque,
 * c'est la PRESENCE — savoir qui est la, ou il se tient, et apprendre sans
 * recharger la page qu'une maison vient d'etre posee.
 *
 * Deux regles de forme, qui expliquent tout le reste:
 *
 * 1. LE JETON NE SORT JAMAIS DU SERVEUR. Un client s'authentifie avec son jeton
 *    (jamais son mot de passe, jamais en clair dans l'URL: c'est le premier
 *    message de la liaison), mais ce que les AUTRES recoivent de lui est un
 *    identifiant de connexion tire au sort, plus son nom de compte. Personne ne
 *    peut donc se faire passer pour quelqu'un d'autre en recopiant ce qu'il a
 *    vu passer.
 * 2. LA BASE RESTE LA VERITE. Un evenement de monde n'est diffuse qu'APRES avoir
 *    ete ecrit; il ne fait que dispenser les autres de recharger. Un client qui
 *    rate un evenement (reseau coupe) rattrape en relisant `GET /api/monde`.
 */
import type { Apparence, EtatJoueur } from './player.ts'
import type { AnimalSauve, Batiment, JardinCueilli, Vehicule } from './monde.ts'

/**
 * Version du protocole. Le serveur refuse une version qu'il ne connait pas
 * plutot que d'interpreter au hasard des champs qui ont change de sens.
 *
 * ELLE NE BOUGE PAS POUR UN AJOUT. Le chat n'ajoutait que des variants nouveaux
 * (`dire`, `chat`): un client d'avant ne les emet jamais et ignore ceux qu'il ne
 * connait pas. La monter aurait renvoye tout onglet ouvert a l'ecran de refus
 * pour une fonction dont il n'a pas besoin.
 *
 * ELLE BOUGE ICI, DE 2 A 3, ET C'EST VOULU. Le deplacement passe en autorite
 * serveur: les champs `x`, `z` et `rot` de `EtatPresence` ne sont plus lus dans
 * le sens client -> serveur, ils sont IGNORES. Un client de version 2 ne sait
 * pas envoyer d'entrees; le laisser entrer, ce serait le laisser immobile a
 * l'endroit ou le serveur croit qu'il est, sans qu'il comprenne pourquoi. Mieux
 * vaut le refus, qui dit de recharger. CONSEQUENCE CONCRETE: au redemarrage du
 * serveur, les onglets deja ouverts sont refuses tant qu'on ne recharge pas la
 * page (Ctrl+Maj+R). Le jeu solo et le mode invite, eux, ne passent pas par le
 * salon et ne sont pas concernes.
 *
 * ELLE BOUGE ENCORE, DE 3 A 4, ET POUR UNE RAISON PLUS SOURNOISE: la FORME du
 * paquet n'a pas change d'un octet, c'est le SENS d'un champ qui a change. `f`
 * portait le facteur de ralentissement (0,55 a 1, « ralentis-moi »); il porte
 * maintenant la forme du joueur (0 a 1, « voici mon energie »), d'ou se
 * deduisent des deux cotes le ralenti a terre et la vitesse de nage.
 *
 * Un onglet reste ouvert sur l'ancien bundle enverrait donc 0,55 en voulant
 * dire « je suis a bout » quand le serveur y lirait « a moitie en forme ». Rien
 * ne planterait: le joueur affame nagerait et marcherait a une vitesse que le
 * serveur ne lui accorde pas, et se ferait recaler a chaque tick sans que rien
 * ne l'explique — la secousse permanente que tout ce fichier existe pour
 * eviter. Un numero qui ne bouge pas parce que « la structure n'a pas change »
 * est exactement le piege: c'est le contrat qui compte, pas la taille.
 *
 * ELLE NE BOUGE PAS POUR `qui`, ET C'EST LE MEME RAISONNEMENT QUE POUR LE CHAT:
 * c'est un variant ADDITIF dans le sens client -> serveur. Un client d'avant ne
 * l'emet jamais, et un serveur d'avant l'ignore (`decoderClient` rend null, le
 * message est ecarte) — il n'y a rien qu'un ancien des deux cotes puisse mal
 * comprendre, seulement une resynchronisation qui n'arrive pas. La monter aurait
 * renvoye tout onglet ouvert a l'ecran de refus pour une reparation dont il n'a
 * pas besoin.
 *
 * LA VERSION 4 PORTE AUSSI L'ETAGE, et elle ne bouge pas pour autant: les deux
 * changements sont dans le meme lot, pas encore deploye. `Entree` gagne un
 * septieme champ (`niv`, l'etage voulu) et `EtatPresence` un champ `niveau`;
 * `CHAMPS_ENTREE` passe donc de 6 a 7, ce qui suffirait a lui seul a rendre un
 * ancien client illisible. Le numero 4 couvre l'ensemble — le monter une
 * seconde fois ne refuserait rien de plus, il ferait seulement croire a deux
 * paliers la ou il n'y en a qu'un.
 *
 * ELLE BOUGE DE 4 A 5 AVEC L'HOTEL. La forme du fil ne change pas d'un octet —
 * `niv` et `niveau` existaient deja — mais leur DOMAINE change: `NIVEAU_MAX`
 * passe de 1 a 2, et un modele nouveau (`hotel`) peut arriver par la diffusion
 * des batiments. Un ancien client bornerait le niveau d'un joueur au toit de
 * l'hotel a 1 et le dessinerait un etage trop bas, et il ne connaitrait pas le
 * modele qu'on lui annonce. Rien ne planterait, tout serait faux en silence —
 * c'est exactement le cas « meme structure, autre contrat » du palier 3 a 4.
 *
 * ELLE BOUGE DE 5 A 6 AVEC LA MINE ET LES AEROPORTS, pour la meme raison, et le
 * lot est un seul palier comme celui de la version 4: QUATRE modeles de
 * batiment nouveaux (`mine`, `aerodrome`, `aeroport`, `aeroport_verre`) et
 * TROIS de vehicule (`avion`, `biplan`, `jet`) circulent desormais dans
 * `GET /api/monde` et dans la diffusion temps reel. Un ancien client les
 * ecarterait un a un (`validerVehicule` refuse un `kind` inconnu, le rendu
 * n'aurait pas de style pour eux): il jouerait dans un monde ampute sans que
 * rien ne le dise. Mieux vaut lui demander de recharger.
 *
 * ELLE BOUGE DE 6 A 7 AVEC LE PARC AQUATIQUE, et pour la raison de tous les
 * paliers precedents: un modele de batiment NOUVEAU (`parc_aquatique`) circule
 * desormais dans `GET /api/monde` et dans la diffusion temps reel. Un ancien
 * onglet n'a pas de style pour lui et l'ecarterait en silence — il jouerait
 * dans un monde ou le parc de son frere n'existe pas, sans qu'un mot le dise,
 * et il traverserait a pied un bassin qu'il ne voit pas. La forme du fil, elle,
 * ne bouge pas d'un octet: `NIVEAU_MAX` reste a 2 (le parc n'a qu'une
 * passerelle, l'hotel garde le record) et les glissades ne voyagent pas — une
 * descente n'est faite que d'entrees ordinaires (voir `cheminGlissade`).
 *
 * ELLE BOUGE DE 7 A 8 AVEC LE DEUXIEME MONDE, et cette fois ce n'est pas un
 * modele de plus: c'est le DOMAINE DES COORDONNEES qui change. La carte ne
 * s'arrete plus a 500 m, elle va jusqu'a 1400 pour englober la deuxieme ile et
 * la mer qui l'en separe. Or `decoderPresence` bornait chaque coordonnee sur
 * `WORLD_SIZE`: un ancien onglet rejetterait comme MESSAGE ABIME toute position
 * au-dela de x = 1000 — il verrait donc ses voisins du monde 2 disparaitre, et
 * le serveur le verrait, lui, refuser des trames parfaitement valides. La
 * structure du fil ne bouge pas d'un octet; le contrat, si. C'est exactement le
 * cas des paliers 3 a 4 et 5 a 6.
 *
 * ELLE BOUGE DE 8 A 9 QUAND L'ILE 2 DEVIENT UNE VRAIE ILE, et la raison est la
 * plus forte de toutes: ce n'est ni un modele de plus, ni meme le domaine des
 * coordonnees — c'est LE SOL LUI-MEME. L'ile 2 est passee de deux cents metres
 * de sable plat a sept cents metres et onze regions, avec des montagnes, un
 * canyon, des lacs et une cote decoupee.
 *
 * Un onglet ouvert avant le deploiement genererait l'ANCIENNE ile, pendant que
 * le serveur fait autorite sur la nouvelle. Il ne verrait pas un monde ampute:
 * il verrait un AUTRE monde. Ses voisins nageraient au travers d'un canyon
 * qu'il dessine en pleine mer, son bonhomme serait recale a chaque tick contre
 * un relief qu'il ne connait pas, et sa grille de navigation tracerait des
 * chemins dans l'eau. C'est la secousse permanente que ce projet a deja payee
 * deux fois — et cette fois, aucun message ne l'expliquerait.
 *
 * A noter, parce que l'argument facile est FAUX: la borne de `decoderPresence`
 * n'aurait rien rejete. Elle vaut `max(CARTE_X, CARTE_Z)` de marge, soit
 * jusqu'a 2800 sur un ancien onglet — les nouvelles coordonnees y passent sans
 * broncher. Le fil n'aurait rien dit du tout, et c'est precisement pourquoi il
 * faut couper: on ne compte pas sur une borne pour signaler un desaccord
 * qu'elle ne mesure pas.
 */
/*
 * De 9 a 10 (13 octobre): LE PAS PARTAGE ARRETE LE PIETON aux troncs et aux
 * rochers (`resoudreNoeuds`, appele par `appliquerEntree`). Meme fil, autre
 * contrat: un ancien onglet traverserait le rocher que le serveur contourne,
 * et serait recale a chaque tronc sans qu'un mot l'explique.
 */
export const VERSION_PROTOCOLE = 10

/** Chemin de la liaison temps reel. Meme port que le reste: pas de proxy. */
export const CHEMIN_WS = '/ws'

/**
 * Ce qu'un client publie de lui-meme, plusieurs fois par seconde.
 *
 * C'est volontairement maigre: de quoi DESSINER un bonhomme ailleurs, plus la
 * seule chose dont la simulation du serveur a besoin de lui — sa position, et
 * la friandise qu'il tend. Ni inventaire, ni besoins, ni jeton.
 *
 * ATTENTION AU SENS DE CIRCULATION. Cette forme sert dans les deux, mais pas
 * pour la meme chose: du SERVEUR vers les clients, `x`, `z` et `rot` sont la
 * verite; du client vers le serveur, ils sont IGNORES depuis que le
 * deplacement est en autorite serveur (voir `entrees` plus bas). Le client
 * continue de les remplir — c'est plus simple que deux formes presque
 * identiques — mais ils ne servent qu'a une chose: poser le joueur la premiere
 * fois, a l'ouverture de sa liaison.
 */
export interface EtatPresence {
  x: number
  z: number
  /** Orientation en radians, 0 = vers +z. */
  rot: number
  etat: EtatJoueur
  nage: boolean
  /** Identifiant du vehicule ou il est assis, sinon null. */
  vehicule: string | null
  /** Identifiant du batiment ou il se trouve, sinon null. */
  interieur: string | null
  /**
   * ETAGE ou il se tient: 0 au sol, 1 sur une terrasse.
   *
   * Il voyage pour une raison de dessin, pas de simulation: le moteur n'a pas
   * de verticalite, donc chacun place les autres a `terrain.hauteur(x, z)`.
   * Sans ce nombre, quelqu'un qui se promene sur son toit apparait chez les
   * autres debout sur la pelouse, a trois metres sous ses pieds. La position
   * qui fait autorite, elle, reste calculee a partir des ENTREES (`niv`).
   */
  niveau: number
  /**
   * Friandise tenue en main, ou null.
   *
   * Elle voyage parce que la FAUNE est simulee par le serveur: sans elle, tendre
   * une carotte a un lapin n'attirerait plus personne dans le monde partage. Le
   * serveur ne verifie pas que le joueur possede vraiment cet objet — au pire il
   * s'attire des animaux, ce qui ne prend rien a personne.
   */
  appat: string | null
}

/** Qui est connecte. Aucune de ces valeurs ne permet de se faire passer pour lui. */
export interface IdentiteJoueur {
  /** Identifiant de CONNEXION, tire au sort par le serveur. Jamais le jeton. */
  id: string
  /** Nom du bonhomme, tel qu'il est affiche au-dessus de sa tete. */
  nom: string
  /** Nom de compte: c'est lui qui donne la couleur (shared/data/proprietaires.ts). */
  compte: string
  apparence: Apparence
}

export interface JoueurConnecte extends IdentiteJoueur {
  etat: EtatPresence
}

/**
 * Un changement du monde deja ecrit en base.
 *
 * Les memes formes que les routes REST correspondantes: le client applique
 * exactement ce qu'il aurait relu dans `GET /api/monde`.
 */
export type EvenementMonde =
  | { quoi: 'batiment-pose'; batiment: Batiment }
  | { quoi: 'batiment-demoli'; id: string }
  | { quoi: 'vehicule-pose'; vehicule: Vehicule }
  | { quoi: 'vehicule-retire'; id: string }
  | { quoi: 'ressource'; ressourceId: number; pv: number; repousseA: number }
  | { quoi: 'jardin'; jardin: JardinCueilli }
  | { quoi: 'animal'; animal: AnimalSauve }
  | { quoi: 'animal-libere'; animalId: number }

/**
 * Une ligne de conversation, telle que le SERVEUR la diffuse.
 *
 * Remarquer ce que le client n'envoie PAS: ni `nom`, ni `compte`. Le protocole
 * ne leur offre aucun champ dans le sens client -> serveur (voir `MessageClient`),
 * et le serveur les remplit lui-meme depuis le jeton qu'il a verifie en base. On
 * ne peut donc pas parler sous le nom d'un autre — c'est a peu pres la seule
 * facon de mentir qu'un chat offrirait.
 */
export interface MessageChat {
  /**
   * Numero croissant attribue par le serveur.
   *
   * Il ne sert qu'a une chose, mais elle est necessaire: apres une coupure, le
   * client recoit de nouveau l'historique et doit reconnaitre les lignes qu'il
   * affiche deja, sinon la conversation se repete a chaque retour du wifi.
   */
  n: number
  /** Nom du bonhomme, tel que le serveur le connait. */
  nom: string
  /** Nom de compte: c'est lui qui donne la couleur (shared/data/proprietaires.ts). */
  compte: string
  texte: string
}

export type MessageClient =
  | { t: 'bonjour'; version: number; token: string }
  | { t: 'etat'; e: EtatPresence }
  /**
   * « Voila ce que j'ai essaye de faire. » Un lot d'entrees numerotees, en
   * entiers compacts (voir shared/sim/entree.ts): direction visee, vitesse
   * voulue, duree du pas. AUCUNE POSITION — c'est tout l'objet du changement.
   * Le serveur les rejoue sur son terrain et repond par `recalage`.
   */
  | { t: 'entrees'; e: number[] }
  /** « Je dis ceci. » Le texte est la SEULE chose que le client fournit. */
  | { t: 'dire'; texte: string }
  /**
   * « Qui est la, au juste ? » Une demande de LISTE COMPLETE, a laquelle le
   * serveur repond par un `bienvenue` — la meme forme qu'a l'ouverture.
   *
   * ELLE REPARE UN TROU REEL. Une identite n'entrait dans la liste des connus
   * que par trois portes: `bienvenue` a l'ouverture, `arrive`, `part`. Or
   * `bienvenue` ne cite que les joueurs deja PLACES: quelqu'un qui se connecte
   * pendant qu'on se presente n'est annonce que par UN SEUL `arrive`. Ce
   * message perdu — une prise qui meurt, une reprise qui coince — et ce joueur
   * envoie des positions dix fois par seconde tout en restant invisible: pas de
   * corps, pas d'etiquette, pas meme un point sur la boussole, pour toujours.
   * Avec Chrome qui gele un onglet cache au bout de cinq minutes, ce n'est pas
   * une hypothese d'ecole.
   *
   * Le message n'a aucun parametre: il n'y a donc rien a mentir, et le serveur
   * ne rend jamais que ce qu'il aurait rendu a l'ouverture.
   */
  | { t: 'qui' }
  /**
   * « Je me couche. » Aucune heure n'est proposee: le serveur decide seul de
   * l'aube a laquelle tout le monde se reveillera. C'est la SEULE facon dont un
   * joueur peut faire avancer l'horloge, et elle n'a pas de parametre a mentir.
   */
  | { t: 'dormir' }

export type MessageServeur =
  | { t: 'bienvenue'; id: string; joueurs: JoueurConnecte[]; tempsJeu: number }
  | { t: 'refus'; raison: string }
  | { t: 'arrive'; joueur: JoueurConnecte }
  | { t: 'part'; id: string }
  | { t: 'presence'; joueurs: { id: string; e: EtatPresence }[] }
  | { t: 'monde'; ev: EvenementMonde }
  /**
   * L'HEURE DU MONDE, tenue par le serveur seul. Le client la suit au lieu de
   * pousser la sienne: sans elle, deux joueurs cote a cote n'etaient pas le
   * meme jour, et l'un lancait des feux d'artifice que l'autre trouvait
   * interdits parce qu'il faisait encore jour chez lui.
   */
  | { t: 'horloge'; tempsJeu: number }
  /**
   * Les animaux du VOISINAGE de ce joueur, en entiers compacts (voir
   * shared/sim/faune-reseau.ts). C'est le serveur qui les fait vivre: tout le
   * monde voit donc la meme bete au meme endroit.
   */
  | { t: 'faune'; a: number[] }
  /**
   * Des lignes de conversation. UN SEUL variant pour deux usages: une ligne
   * qu'on vient de dire, ou les dernieres lignes envoyees a qui arrive. Le
   * client les traite de la meme facon, il n'a donc rien a distinguer.
   */
  | { t: 'chat'; messages: MessageChat[] }
  /**
   * TA POSITION, telle que le serveur la connait, et le numero de la derniere
   * entree qu'il a prise en compte.
   *
   * Ce message n'est envoye qu'a son destinataire, et c'est ce qui ferme la
   * boucle: le client repart de cette position, rejoue les entrees encore en
   * vol par-dessus, et resorbe l'ecart en douceur (voir `Reconciliation` dans
   * shared/sim/entree.ts). Le numero est indispensable — sans lui, le client ne
   * saurait pas quelles entrees sont deja comprises dans ce qu'il recoit, et
   * les compterait deux fois.
   */
  | { t: 'recalage'; n: number; x: number; z: number; rot: number }
  /**
   * « DESCENDS DE CE VEHICULE. » Le serveur refuse le volant qu'un client vient
   * de s'attribuer, et dit pourquoi en francais affichable tel quel.
   *
   * Il existe parce que monter etait une decision purement locale: rien ne
   * comptait les occupants, et deux joueurs ont pilote la meme voiture, chacun
   * la tirant vers son propre joystick. Le serveur est le seul a voir tout le
   * monde a la fois, donc c'est lui qui attribue la place — et le refus doit
   * revenir a l'interesse, sinon il continue de croire qu'il conduit.
   *
   * Variant ADDITIF, dans le sens serveur -> client seulement: un client d'hier
   * l'ignore et se comporte comme avant. `VERSION_PROTOCOLE` ne bouge pas.
   */
  | { t: 'volant'; id: string; raison: string }
